<template>
  <div class="round-details mt-6 mb-12" v-resize="onResize">
    <v-navigation-drawer
      v-model="dialog"
      class="shot-info-dialog"
      width="331"
      fixed
      right
      dark
      hide-overlay
      disable-resize-watcher
      disable-route-watcher
    >
      <v-list class="pb-0">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6">Shot Information</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click.stop="closeDialog">
              <v-icon>close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-list class="pb-0" v-if="dialog">
        <Shotview :shotIDProp="shotIDProp" singlePlayer mobileLayout />
      </v-list>
      <v-list v-else style="height: 50vh"></v-list>
    </v-navigation-drawer>

    <v-layout wrap v-if="loadingRounds">
      <v-flex xs12>
        <div class="progress progress-main" v-if="loadingRounds">
          <pulse-loader :color="$options.loaderColor" :size="$options.loaderSize"></pulse-loader>
        </div>
      </v-flex>
    </v-layout>

    <v-container v-else fluid grid-list-md text-left ref="cardContainer">
      <transition name="fadeDown" appear>
        <CourseCard
          :details="roundDetails"
          :user="this.$route.params.user || user"
          :scorecard="scoreCardData"
          :score="score"
          :isStrokePlay="isStrokePlay"
          :isScramble="isScramble"
          :gameMode="gameMode"
          :showScorecard="showScorecard"
        />
      </transition>

      <transition name="fadeDown" appear>
        <v-tabs
          v-model="active"
          background-color="primary"
          slider-color="#FFFFFF"
          dark
          class="round-details-tabs"
        >
          <v-tab v-for="item in tabItems" :key="item.value" ripple>
            {{ $t(`roundDetails.${item.value}`) }}
          </v-tab>

          <v-spacer></v-spacer>

          <v-flex xs4 sm6>
            <v-layout wrap justify-end fill-height class="mt-1">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <ShareNetwork
                    network="email"
                    :url="meta.url"
                    :title="meta.title"
                    :description="meta.description"
                    :media="meta.image"
                  >
                    <v-btn v-bind="attrs" v-on="on" icon dark class="mt- mr-4">
                      <v-icon dark> mdi-email </v-icon>
                    </v-btn>
                  </ShareNetwork>
                </template>
                <span>Email Round Details</span>
              </v-tooltip>

              <v-flex xs6 sm2 md2>
                <p class="poll">{{ $t('roundDetails.polling') }}:</p>
              </v-flex>
              <v-flex xs6 sm2 md2>
                <v-switch
                  inset
                  :color="$vuetify.theme.dark ? 'white' : ''"
                  v-model="polling"
                  @click.native="togglePolling"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-flex>

          <!-- SCORECARD ====================================== -->
          <v-tab-item v-for="item in tabItems" :key="item.id">
            <div
              class="progress progress-main"
              v-if="item.value === 'scorecard' && showScorecard && scoreCardLoading"
            >
              <pulse-loader
                :color="$options.loaderColor"
                :size="$options.loaderSize"
              ></pulse-loader>
            </div>

            <v-card
              flat
              v-else-if="
                item.value === 'scorecard' && showScorecard && !loadingShotData && !scoreCardLoading
              "
            >
              <transition-group name="fadeDown" appear>
                <div
                  v-if="!loadingShotData && scoreCardData"
                  class="hidden-xs-only scorecard-container"
                  :key="1"
                >
                  <ScoreCard
                    :userShots="scoreCardData"
                    :shotsLoading="userShotsLoading"
                    :gameMode="roundGameMode"
                    expanded
                  ></ScoreCard>
                </div>
                <v-flex
                  xs12
                  hidden-sm-and-up
                  class="text-center pt-6 pb-2-0 scorecard-rotate"
                  :key="2"
                >
                  <v-icon>screen_rotation</v-icon>
                  <p>{{ $t('roundDetails.rotate') }}</p>
                </v-flex>
              </transition-group>
            </v-card>

            <v-card
              flat
              v-else-if="item.value === 'scorecard' && !loadingShotData && !showScorecard"
            >
              <v-card-text>
                <v-alert :value="true" outlined type="info" color="info">
                  {{ $t('roundDetails.scorecardNA') }}
                  <b>
                    <em>{{ roundDetails.gameMode }}</em>
                  </b>
                  {{ $t('roundDetails.rounds') }}
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- STATISTICS ====================================== -->
            <v-card flat v-if="item.value === 'statistics'">
              <div class="progress progress-main" v-if="loadingShotData">
                <pulse-loader
                  :color="$options.loaderColor"
                  :size="$options.loaderSize"
                ></pulse-loader>
              </div>
              <transition name="fadeDown" appear v-else>
                <v-card-text class="px-0">
                  <v-layout wrap>
                    <!-- DRIVING ACCURACY -->
                    <v-flex xs12 sm6 md3 class="py-6" v-if="!loadingShotData">
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#5a95c8' }"
                        >
                          <!-- {{ drivingAccuracy.drivingAccuracyPercentage | utility_percentage }} -->
                          {{ drivingAccuracy.datasets[0].data[0] | utility_percentage }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chartData="drivingAccuracy"
                            :chartOptions="drivingAccuracyOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="drivingAccuracy"
                            :chartOptions="drivingAccuracyOptions"
                            chartType="doughnut"
                            chartID="222"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- GREENS IN REGULATION -->
                    <v-flex xs12 sm6 md3 class="py-6" v-if="!loadingShotData">
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#78b058' }"
                        >
                          <!-- {{ greensInRegulation.girPercentage | utility_percentage }} -->
                          {{ greensInRegulation.datasets[0].data[0] | utility_percentage }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chartData="greensInRegulation"
                            :chartOptions="greensInRegulationOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="greensInRegulation"
                            :chartOptions="greensInRegulationOptions"
                            chartType="doughnut"
                            chartID="333"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- DRIVING DISTANCE -->
                    <v-flex xs12 sm6 md3 class="py-6" v-if="!loadingShotData">
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#ba2127' }"
                        >
                          {{ drivingDistance.datasets[0].data[0] | nan }}
                          {{ drivingDistance.datasets[0].data[0] ? ydsUnitType : '' }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chartData="drivingDistance"
                            :chartOptions="drivingDistanceOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="drivingDistance"
                            :chartOptions="drivingDistanceOptions"
                            chartType="doughnut"
                            chartID="444"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- PUTTING DISTANCE -->
                    <v-flex xs12 sm6 md3 class="py-6" v-if="!loadingShotData">
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#231f20' }"
                        >
                          {{ puttingDistance.datasets[0].data[0] | nan }}
                          {{ puttingDistance.datasets[0].data[0] ? ftUnitType : '' }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chartData="puttingDistance"
                            :chartOptions="puttingDistanceOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="puttingDistance"
                            :chartOptions="puttingDistanceOptions"
                            chartType="doughnut"
                            chartID="555"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- AREA CHART/LINE CHART - Show both charts for stroke play -->
                    <v-flex xs12 v-if="isStrokePlay">
                      <transition name="fadeDown" appear>
                        <LineChart
                          v-if="!loadingShotData"
                          class="area-chart"
                          :chartData="roundParChartData"
                          :chartOptions="areaParChartOptions"
                          :height="!isMobile ? 100 : 300"
                        ></LineChart>
                      </transition>
                      <transition name="fadeDown" appear>
                        <LineChart
                          v-if="!loadingShotData && hasShotGraphData"
                          class="line-chart mt-6"
                          :chartData="shotDistance"
                          :chartOptions="lineChartOptions"
                          :height="!isMobile ? 100 : 300"
                        ></LineChart>
                      </transition>
                    </v-flex>

                    <!-- LINE CHART for non-stroke play rounds -->
                    <v-flex xs12 v-else-if="!isStrokePlay">
                      <transition name="fadeDown" appear>
                        <LineChart
                          v-if="!loadingShotData"
                          class="line-chart"
                          :chartData="shotDistance"
                          :chartOptions="lineChartOptions"
                          :height="!isMobile ? 100 : 300"
                        ></LineChart>
                      </transition>
                    </v-flex>

                    <!-- FILTER CLUB OPTION -->
                    <v-flex xs12 class="my-4" v-if="userShots && userShots.length">
                      <transition name="fadeDown" appear>
                        <v-layout justify-start fill-height wrap>
                          <v-flex xs3 sm2 md1 class="text-center text-sm-right mr-1">
                            <p class="filter-by">{{ $t('roundDetails.filterBy') }}:</p>
                          </v-flex>
                          <v-flex xs7 sm3 md2>
                            <v-select
                              :items="clubTypes"
                              class="filter-box"
                              v-model="filteredClubType"
                              label="Club Type"
                              single-line
                            ></v-select>
                          </v-flex>
                          <!-- <v-flex xs4 sm2 md1 class="text-center text-sm-right mr-1"> -->
                          <!-- <p class="units">{{ $t('roundDetails.filterBy') }}:</p> -->
                          <!-- <p class="units">Unit Measurement:</p> -->
                          <!-- </v-flex> -->

                          <!-- <v-flex xs7 sm3 md2>
                            <v-select
                              :items="unitTypeOptions"
                              class="units-box"
                              v-model="unitType"
                              label="Measurement"
                              single-line
                            ></v-select>
                          </v-flex> -->

                          <v-flex xs5 sm2 class="text-sm-right text-left mr-1">
                            <p class="filter-by">{{ $t('roundDetails.measurement') }}:</p>
                          </v-flex>
                          <v-flex xs6 sm3 md3>
                            <v-radio-group v-model="unitType" class="mt-4" row hide-details>
                              <v-radio
                                v-for="n in unitTypeOptions"
                                :key="n"
                                :label="`${n}`"
                                :value="n"
                              ></v-radio>
                            </v-radio-group>
                          </v-flex>
                        </v-layout>
                      </transition>
                    </v-flex>

                    <!-- DATA TABLE OF SHOTS -->
                    <v-flex xs12 class="mt-0">
                      <transition name="fadeDown" appear>
                        <v-card flat class="data-table light-border">
                          <v-data-table
                            class="shot-data-table table-striped"
                            :headers="convertedHeaders"
                            :items="userShots"
                            :loading="loadingShotData"
                            :item-class="mulliganClass"
                            :footer-props="{
                              'items-per-page-options': [10, 15, { text: 'All', value: -1 }],
                              'items-per-page-text': 'Shots per page:',
                            }"
                            @click:row="shotClick"
                          >
                            <template v-slot:progress>
                              <v-progress-linear
                                slot:progress
                                color="info"
                                height="10"
                                indeterminate
                              ></v-progress-linear>
                            </template>

                            <template v-slot:[`item.courseHoleIndex`]="{ item }">
                              {{ item.courseHoleIndex + 1 }}
                            </template>
                            <template v-slot:[`item.shotIndex`]="{ item }">
                              <span :class="{ active: isActive === item.id }">
                                {{ item.shotIndex + 1 }}
                              </span>
                            </template>
                            <template v-slot:[`item.clubTypeKey`]="{ item }">
                              <span :class="{ active: isActive === item.id }">
                                {{ keyToFriendly(item.clubTypeKey) }}
                              </span>
                            </template>
                            <!-- Head Speed default fps -->
                            <template v-slot:[`item.clubHeadSpeed`]="{ item }">
                              {{ unitConversion(unitType, item.clubHeadSpeed, 'fps') }}
                              <!-- unitConversion(unitType, Math.abs(roundNum(item.clubHeadSpeed * 0.681818))) -->
                              <!-- {{
                                Math.abs(roundNum(item.clubHeadSpeed * 0.681818))
                              }} -->
                            </template>
                            <!-- Distance - feet -->
                            <template v-slot:[`item.totalDistance`]="{ item }">
                              {{ unitConversion(unitType, item.totalDistance, 'yds') }}
                              <!-- {{ roundNum(item.totalDistance / 3) }} -->
                            </template>
                            <!-- Carry -->
                            <template v-slot:[`item.carryDistance`]="{ item }">
                              {{ unitConversion(unitType, item.carryDistance, 'yds') | nan }}
                              <!-- {{ roundNum(item.carryDistance / 3) | nan }} -->
                            </template>
                            <!-- Ball Speed -->
                            <template v-slot:[`item.launchBallSpeed`]="{ item }">
                              {{ unitConversion(unitType, item.launchBallSpeed, 'fps') }}
                              <!-- {{ roundNum(item.launchBallSpeed * 0.681818) }} -->
                            </template>
                            <template v-slot:[`item.launchBackSpin`]="{ item }">
                              {{ roundNum(item.launchBackSpin) }}
                            </template>
                            <template v-slot:[`item.launchSideSpin`]="{ item }">
                              {{ Math.abs(roundNum(item.launchSideSpin)) }}
                              <v-icon v-if="item.launchSideSpin < 0" small> arrow_back </v-icon>
                              <v-icon v-if="item.launchSideSpin > 0" small> arrow_forward </v-icon>
                            </template>
                            <template v-slot:[`item.launchDirection`]="{ item }">
                              {{ Math.abs(roundNum(item.launchDirection)) }}&deg;
                              <v-icon v-if="item.launchDirection < 0" small> arrow_back </v-icon>
                              <v-icon v-if="item.launchDirection > 0" small> arrow_forward </v-icon>
                            </template>
                            <template v-slot:[`item.launchAngle`]="{ item }">
                              {{ roundNum(item.launchAngle) }}&deg;
                            </template>
                          </v-data-table>
                        </v-card>
                      </transition>
                    </v-flex>

                    <!-- ROUND PARTICIPANTS -->
                    <v-flex xs12 v-if="roundPlayers && roundPlayers.length > 1 && !loadingShotData">
                      <transition name="fadeDown">
                        <v-card
                          xs12
                          flat
                          class="players mt-2 mb-4"
                          v-if="roundPlayers && roundPlayers.length > 1 && !loadingShotData"
                        >
                          <v-card-title primary-title>
                            <div class="text-h6">{{ $t('roundDetails.roundParticipants') }}</div>
                          </v-card-title>

                          <div class="progress" v-if="loadingShotData">
                            <pulse-loader
                              :color="$options.loaderColor"
                              :size="$options.loaderSize"
                            ></pulse-loader>
                          </div>

                          <v-data-table
                            :headers="playerHeaders"
                            :items="roundPlayers"
                            @click:row="clickedGolferRow"
                            hide-default-footer
                          >
                            <template v-slot:[`item.name`]="{ item }">
                              <span class="text-left golfer fillTD">
                                {{ item.name }}
                              </span>
                            </template>
                            <template v-slot:[`item.id`]="{ item }">
                              <span class="text-left golfer fillTD">
                                {{ item.id.substr(0, 8) }}
                              </span>
                            </template>
                            <template v-slot:[`item.teeLevel`]="{ item }">
                              <span class="text-left golfer fillTD">
                                {{ item.teeLevel }}
                              </span>
                            </template>
                          </v-data-table>
                        </v-card>
                      </transition>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </transition>
            </v-card>

            <!-- ANALYTICS ====================================== -->
            <!-- Letting everyone view analytics right now -->
            <!-- <v-card flat v-if="item.value === 'analytics' && active === 2 && isMe && hasAnalytics"> -->
            <v-card
              flat
              class="analytics-card"
              v-if="item.value === 'analytics' && active === 2 && isMe"
            >
              <div class="progressAA" v-if="loadingAA">
                <pulse-loader
                  :color="$options.loaderColor"
                  :size="$options.loaderSize"
                ></pulse-loader>
              </div>
              <transition name="fadeDown" appear>
                <AdvancedAnalytics :roundID="roundID" :userID="userID" />
              </transition>
            </v-card>

            <!-- Display this when we turn on permissions for analytics -->
            <!-- <v-card
            flat
            v-else-if="item.value === 'analytics' && active === 2 && isMe && !hasAnalytics"
          >
            <transition name="fadeDown" appear>
              <v-layout align-center justify-center row wrap fill-height class="py-12">
                <v-flex xs12 sm5 md5>
                  <v-card-title
                    class="display-1 font-weight-bold primary--text text-uppercase py-0"
                  >
                    Advanced Analytics
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <span class="title grey--text text--darken-2 text-uppercase">
                      Analyze your round
                    </span>
                  </v-card-text>
                  <v-card-text class="py-6">
                    Subscribe for access to in-depth analytics. With this tool you can sort and
                    compare every shot from two different rounds or see an aggregate table of all
                    shots selected. Visualize your data over 18 holes, with a top-down view of shots
                    selected.
                  </v-card-text>
                  <v-card-actions class="px-6">
                    <v-btn color="primary" large @click="subscribeAA">Subscribe</v-btn>
                  </v-card-actions>
                </v-flex>
                <v-flex xs12 sm6 md5>
                  <v-img :src="`${AASample}`" alt="Advanced Analytics" />
                </v-flex>
              </v-layout>
            </transition>
          </v-card> -->

            <v-card flat v-else-if="item.value === 'analytics' && active === 2 && !isMe">
              <transition name="fadeDown" appear>
                <v-alert outlined type="info" color="info">
                  {{ $t('roundDetails.advAnalyticsVisibile') }}
                </v-alert>
              </transition>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </transition>

      <v-btn fixed bottom left fab dark small color="primary" @click="back">
        <v-icon dark>arrow_back</v-icon>
      </v-btn>

      <!-- <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <ShareNetwork
            network="email"
            :url="meta.url"
            :title="meta.title"
            :description="meta.description"
            :media="meta.image"
          >
            <v-btn
              v-bind="attrs"
              v-on="on"
              style="left: 65px"
              fixed
              bottom
              fab
              dark
              small
              color="success"
            >
              <v-icon dark> mdi-email </v-icon>
            </v-btn>
          </ShareNetwork>
        </template>
        <span>Email Round Details</span>
      </v-tooltip> -->
    </v-container>
  </div>
</template>

<script>
// TODO REFACTOR OUT INTO SEPARATE COMPONENTS !!!!!!!!!!!
import { mapGetters } from 'vuex';
import meMixin from '@/mixins/meMixin';
import utilityMixin from '@/mixins/utilityMixin';
import ScoreCard from '@/components/ScoreCard';
import CourseCard from '@/components/CourseCard';
import AdvancedAnalytics from '@/components/AdvancedAnalytics';
import Shotview from '@/components/Shotview';
import DoughnutChart from '@/components/charts/DoughnutChart';
import LineChart from '@/components/charts/LineChart';
// import Chart from '@/components/charts/newCharts/Chart';
import AASample from '@/assets/AA-sample@2x.png';
import scorecardDataConfig from '../lib/scorecardDataConfig';
import browserDetector from '../lib/browserDetector';
import { loaderColor, loaderSize, scoreCardModes } from '@/lib/constants';
import { unitConversion } from '@/utils/helpers';
import {
  baseDoughnutOptions,
  baseDoughnutBGColor,
  baseChartOptions,
  baseLineChartOptions,
} from '@/lib/chartData.js';

export default {
  name: 'RoundDetails',
  mixins: [meMixin, utilityMixin],
  components: {
    ScoreCard,
    CourseCard,
    AdvancedAnalytics,
    Shotview,
    DoughnutChart,
    LineChart,
    // Chart,
  },

  metaInfo() {
    const url = window?.location?.href;
    const { displayName = '' } = this.me;
    const { title } = this.roundDetails;
    const imgOptions = `?w=1200&h=628&fit=crop&auto=compress,format&txt=${encodeURIComponent(
      title
    )}&txt-color=f8f9ff&txt-font=Helvetica%20Neue&txt-size=36&txt-align=right,bottom&txt-shad=5&txt-pad=20`;
    const formattedImg = `${this.roundDetails?.course?.imageURL}${imgOptions}`;

    this.meta.title = title;
    this.meta.url = url;
    this.meta.image = formattedImg;
    this.meta.description = `E6 Golf - ${displayName} round details and shot statistics.`;

    return {
      title: `${displayName} Round Details`,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: `E6 Golf - ${displayName} round details and shot statistics.`,
        },
        //* Facebook
        {
          name: 'og:type',
          content: 'website',
        },
        {
          vmid: 'og:site_name',
          name: 'og:site_name',
          content: 'E6 Golf Portal',
        },
        {
          vmid: 'og:title',
          name: 'og:title',
          content: `${displayName} Round Details`,
        },
        {
          vmid: 'og:description',
          name: 'og:description',
          content: `E6 Golf - ${displayName} round details and shot statistics.`,
        },
        {
          vmid: 'og:url',
          name: 'og:url',
          content: url,
        },
        {
          vmid: 'og:image',
          name: 'og:image',
          content: formattedImg,
        },
        //* Twitter
        {
          vmid: 'twitter:title',
          name: 'twitter:title',
          content: `${displayName} Round Details`,
        },
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: `E6 Golf - ${displayName} round details and shot statistics.`,
        },
        {
          vmid: 'twitter:url',
          name: 'twitter:url',
          content: url,
        },
        {
          vmid: 'twitter:image',
          name: 'twitter:image',
          content: formattedImg,
        },
      ],
    };
  },

  data() {
    return {
      meta: {
        title: '',
        image: '',
        url: '',
        description: '',
      },

      AASample,
      isActive: '',
      dialog: false,
      shotIDProp: null,
      score: null,
      analyticsVisible: false,
      active: null,
      loadingAA: false,
      calcLeft: '40vw',
      shotData: [],
      loadingShotData: false,
      tabItems: [
        { value: 'scorecard', tab: 'Scorecard' },
        { value: 'statistics', tab: 'Statistics' },
        { value: 'analytics', tab: 'Advanced Analytics' },
      ],
      isMobile: false,
      search: '',
      pollingHandler: null,
      filteredClubType: 'All',
      unitType: `${this.$t('roundDetails.imperial')}`,
      // unitTypeOptions: ['Imperial', 'Metric'],
      // unitTypeOptions: [`${this.$t('roundDetails.imperial')}`, `${this.$t('roundDetails.metric')}`],
      polling: false,
      userShotsLoading: false,
      scoreCardData: {},
      scoreCardLoading: false,
      roundGameMode: '',
      pars: [],
      playerHeaders: [
        { text: this.$t('roundDetails.name'), align: 'left', value: 'name' },
        { text: this.$t('roundDetails.playerID'), align: 'left', value: 'id' },
        { text: this.$t('roundDetails.teeLevel'), align: 'left', value: 'teeLevel' },
      ],
      headers: [
        { text: 'Hole', align: 'left', value: 'courseHoleIndex' },
        { text: 'Shot', align: 'left', value: 'shotIndex' },
        { text: 'Club Type', value: 'clubTypeKey' },
        { text: 'Head Speed (mph)', value: 'clubHeadSpeed' },
        { text: 'Distance (yds)', value: 'totalDistance' },
        { text: 'Carry (yds)', value: 'carryDistance' },
        { text: 'Ball Speed (mph)', value: 'launchBallSpeed' },
        { text: 'Back Spin (rpm)', value: 'launchBackSpin' },
        { text: 'Side Spin (rpm)', value: 'launchSideSpin' },
        { text: 'Launch Direction', value: 'launchDirection' },
        { text: 'Launch Angle', value: 'launchAngle' },
      ],
      clubTypes: ['All'],
    };
  },

  beforeCreate() {
    this.$store.dispatch('shots/resetState');
  },

  created() {
    //* non-reactive constants
    this.$options.loaderColor = loaderColor;
    this.$options.loaderSize = loaderSize;
    this.$options.scoreCardModes = scoreCardModes;
  },

  mounted() {
    this.scoreCardLoading = true;
    this.loadingShotData = true;
    this.unitType =
      localStorage.getItem('e6UserMeasurement') !== 'undefined' &&
      localStorage.getItem('e6UserMeasurement') !== null
        ? localStorage.getItem('e6UserMeasurement')
        : 'Imperial';
    //* set local storage
    localStorage.setItem('e6UserMeasurement', this.unitType);

    this.$store
      .dispatch('rounds/show', { id: this.$route.params.id })
      .then((res) => {
        const { gameMode = '', players = [] } = res;
        this.roundGameMode = gameMode;
        const displayScoreCard = this.$options.scoreCardModes.includes(gameMode);

        const currentPlayer = players.find((player) => {
          return player.id === this.$route.params.userID;
        });

        const currentPlayerRoundHCP = currentPlayer?.handicap?.toFixed(1) ?? null;

        //* Only get scorecard data for stroke play rounds
        if (displayScoreCard) {
          const player = {
            user_uuid: this.$route.params.userID,
            round_uuid: this.$route.params.id,
            handicap:
              currentPlayerRoundHCP || currentPlayerRoundHCP === 0 || currentPlayerRoundHCP === null
                ? currentPlayerRoundHCP
                : this.user?.handicap
                ? this.user?.handicap
                : 0,
            gender: this.user?.gender,
            gameMode: this.roundGameMode,
            players: this.roundPlayers,
            guestPlayers: this.roundGuestPlayers,
          };
          this.userScorecardData(player);
        }

        //* Set statistics tab to active if not stroke play round
        if (!displayScoreCard) {
          this.active = 1;
        }
      })
      .catch((error) => {
        console.error('error:', error);
        this.$root.vtoast.show({
          text: `Error retrieving round details`,
          color: 'error',
        });
      });

    const payload = {
      roundID: this.$route.params.id,
      userID: this.$route.params.userID,
    };
    this.$store
      .dispatch('shots/all', payload)
      .then((res) => {
        if (res?.length) {
          const shots = res.filter((shot) => {
            return shot.userID === this.$route.params.userID;
          });
          this.score = shots ? shots[shots?.length - 1]?.roundScore : null;
          this.shotData = shots;
        }
      })
      .then(() => {
        this.loadingShotData = false;
      })
      .catch((err) => {
        console.log('err:', err);
      });

    if (
      (this.$route?.params?.userID !== 'undefined' || this.$route?.params?.userID !== undefined) &&
      this.user?.id !== this.$route?.params?.userID
    ) {
      this.$store.dispatch('users/show', this.$route?.params?.userID);
      this.$store.dispatch('users/stats', this.$route?.params?.userID);
    }
  },

  beforeDestroy() {
    this.destroyPolling(this.pollingHandler);
    // clearInterval(this.pollingHandler);
    this.$store.dispatch('shots/resetState');
  },

  computed: {
    ...mapGetters({
      round: 'rounds/roundDetails',
      roundList: 'rounds/roundList',
      loadingRounds: 'rounds/loading',
      loading: 'shots/loading',
      shotList: 'shots/shotList',
      user: 'users/user',
      stats: 'users/stats',
      hasAnalytics: 'users/hasAnalytics',
    }),

    unitTypeOptions() {
      return [`${this.$t('roundDetails.imperial')}`, `${this.$t('roundDetails.metric')}`];
    },

    convertedHeaders() {
      // if (this.unitType === 'Metric') {
      if (this.unitType === this.$t('roundDetails.metric')) {
        this.headers.map((item) => {
          if (item.text.includes('(mph)')) {
            item.text = item.text.replace('mph', 'kph');
          }
          if (item.text.includes('(yds)')) {
            item.text = item.text.replace('(yds)', '(m)');
          }
          return item;
        });
      }
      // if (this.unitType === 'Imperial') {
      if (this.unitType === this.$t('roundDetails.imperial')) {
        this.headers.map((item) => {
          if (item.text.includes('(kph)')) {
            item.text = item.text.replace('kph', 'mph');
          }
          if (item.text.includes('(m)')) {
            item.text = item.text.replace('(m)', '(yds)');
          }
          return item;
        });
      }
      return this.headers;
    },

    gameMode() {
      if (this.round) {
        return this.round?.gameMode;
      }
    },

    isMe() {
      return this.me?.id === this.$route.params.userID;
    },

    holeCount() {
      if (this.scoreCardData?.greensInRegulation) {
        return this.scoreCardData?.holeScores?.filter((data) => {
          return data !== null;
        }).length;
      }
    },

    roundID() {
      if (this.$route.params.id) {
        return this.$route.params.id;
      }
    },

    userID() {
      if (this.$route.params.userID) {
        return this.$route.params.userID;
      }
    },

    doughnutBGColor() {
      return baseDoughnutBGColor(this.$vuetify.theme.dark);
    },

    doughnutOptions() {
      return baseDoughnutOptions(this.$vuetify.theme.dark);
    },

    //* Driving Accuracy Doughnut Chart Setup ==================
    drivingAccuracy() {
      const shotList = this?.shotData ?? [];
      const teeShots = [];
      let fairwayShot = 0;
      let accuracy = 0;

      if (shotList.length) {
        shotList.forEach((shot) => {
          const { clubTypeKey, mulligan, score, userID } = shot;

          //* Not sure why we're excluding score === 0
          // if (!shot.totalDistance || clubTypeKey === 'Putter' || mulligan || score === 0) return;
          if (!shot.totalDistance || clubTypeKey === 'Putter' || mulligan) return;

          if (userID === this.$route.params.userID) {
            if (shot.startedOnTee) {
              teeShots.push(shot);
              const finalShot = shot?.snapshots?.find((data) => {
                return data.type === 'total';
              });
              //* Calculate fairway shot
              this.checkTerrain(finalShot, 'kTerrainFairway') ? (fairwayShot += 1) : null;
            }
          }
        });
        accuracy = teeShots ? fairwayShot / teeShots.length : 0;
      }

      return {
        labels: [this.$t('roundDetails.round'), this.$t('roundDetails.overall')],
        datasets: [
          {
            data: [
              !isNaN(accuracy) ? accuracy : 0,
              this.stats === undefined ? 0 : this.stats?.DrivingAccuracy?.toFixed(2),
            ],
            backgroundColor: ['#5a95c8', this.doughnutBGColor],
          },
        ],
        drivingAccuracyPercentage: this.stats === undefined ? 0 : this.stats?.DrivingAccuracy,
      };
      //* Below returns the overall accuracy stats instead of the round stats if that is wanted
      // return {
      //   labels: [`Valid Drives`, `Total Drives`],
      //   datasets: [
      //     {
      //       data: [
      //         Math.round(this.stats === undefined ? 0 : this.stats?.DrivingAccuracyValid),
      //         Math.round(this.stats === undefined ? 0 : this.stats?.DrivingAccuracyTotal),
      //       ],
      //       backgroundColor: ['#5a95c8', 'rgba(0, 0, 0, 0.2)'],
      //     },
      //   ],
      //   drivingAccuracyPercentage: this.stats === undefined ? 0 : this.stats?.DrivingAccuracy,
      // };
    },

    drivingAccuracyOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: this.$t('roundDetails.driveAccuracy'),
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* GIR Doughnut Chart Setup ==================
    greensInRegulation() {
      let gir = [];
      let holeCount = [];

      if (this.scoreCardData?.greensInRegulation) {
        holeCount = this.scoreCardData?.holeScores?.filter((data) => {
          return data !== null;
        });
        gir = this.scoreCardData?.greensInRegulation?.filter((data) => {
          return data === true;
        });
      }
      const girCalc = gir && holeCount ? (gir?.length / holeCount?.length).toFixed(2) : 0;

      const chartData = {
        labels: [this.$t('roundDetails.round'), this.$t('roundDetails.overall')],
        datasets: [
          {
            data: [
              // gir && holeCount ? (gir?.length / holeCount?.length).toFixed(2) : 0,
              !isNaN(girCalc) ? girCalc : 0,
              this.stats === undefined ? 0 : this.stats?.GreenInRegulation?.toFixed(2),
            ],
            backgroundColor: ['#78b058', this.doughnutBGColor],
          },
        ],
      };
      //* Below returns the overall GIR stats instead of the round stats if that is wanted
      // return {
      //   labels: [`Valid Shots`, `Overall`],
      //   datasets: [
      //     {
      //       data: [
      //         Math.round(this.stats === undefined ? 0 : this.stats?.GreenInRegulationValid),
      //         this.stats === undefined ? 0 : this.stats?.GreenInRegulation?.toFixed(2),
      //       ],
      //       backgroundColor: ['#78b058', this.doughnutBGColor],
      //     },
      //   ],
      //   girPercentage: this.stats === undefined ? 0 : this.stats?.GreenInRegulation,
      // };

      //* Check if data is all 0s; if it is, add dummy data to end with empty label
      chartData.datasets.forEach((dataset) => {
        if (
          dataset.data.every((el) => {
            return el === 0 || el === '0.00';
          })
        ) {
          dataset.data = [0, 0.0001];
        }
      });

      return chartData;
    },

    greensInRegulationOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: this.$t('roundDetails.gir'),
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* Driving Distance Doughnut Chart Setup ==================
    drivingDistance() {
      const shotList = this?.shotData ?? [];
      const teeShots = [];
      let roundBest = 0;

      if (shotList.length) {
        shotList.forEach((shot) => {
          const { clubTypeKey, mulligan, score, userID } = shot;

          //* Not sure why we're excluding score === 0
          // if (!shot.totalDistance || clubTypeKey === 'Putter' || mulligan || score === 0) return;
          if (!shot.totalDistance || clubTypeKey === 'Putter' || mulligan) return;

          if (userID === this.$route.params.userID) {
            if (shot.startedOnTee) {
              teeShots.push(shot);
            }
          }
          roundBest = teeShots.reduce((prev, curr) => {
            return curr.totalDistance > prev ? curr.totalDistance : prev;
          }, 0);
        });
      }

      // TODO Is there a way to spell out average and not wrap?
      const chartData = {
        labels: [
          this.$t('roundDetails.round'),
          `${this.$t('roundDetails.overall')} ${this.$t('roundDetails.avg')}`,
        ],
        datasets: [
          {
            //* value in feet - divide by 3 to get yards
            data: [
              // Math.round(roundBest / 3.0),
              Math.round(unitConversion(this.unitType, roundBest, 'yds')),
              // Math.round(this.stats === undefined ? 0 : this.stats?.DrivingDistanceAverage / 3.0),
              Math.round(
                this.stats === undefined
                  ? 0
                  : this.unitConversion(this.unitType, this.stats?.DrivingDistanceAverage, 'yds')
              ),
            ],
            backgroundColor: ['#ba2127', this.doughnutBGColor],
          },
        ],
      };

      //* Check if data is all 0s; if it is, add dummy data to end with empty label
      chartData.datasets.forEach((dataset) => {
        if (
          dataset.data.every((el) => {
            return el === 0 || el === '0.00';
          })
        ) {
          dataset.data = [0, 0.0001];
        }
      });

      return chartData;
    },

    drivingDistanceOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: `${this.$t('roundDetails.driveDist')} (${
              this.unitType === this.$t('roundDetails.imperial') ? 'yds' : 'm'
            })`,
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* Putting Distance Doughnut Chart Setup ==================
    puttingDistance() {
      const shotList = this?.shotData ?? [];
      const putts = [];
      let roundBest = 0;

      if (shotList.length) {
        shotList.forEach((shot) => {
          const { clubTypeKey, userID = '' } = shot;
          if (!shot.totalDistance || clubTypeKey === 'Putter') {
            if (userID === this.$route.params?.userID && shot?.endedInHole) {
              putts.push(shot);
            }
          }

          roundBest = putts.reduce((prev, curr) => {
            return curr?.totalDistance > prev ? curr.totalDistance : prev;
          }, 0);
        });
      }

      return {
        //* change to Avg
        labels: [
          this.$t('roundDetails.round'),
          // `${this.$t('roundDetails.overall')} ${this.$t('roundDetails.average')}`,
          `${this.$t('roundDetails.overall')} ${this.$t('roundDetails.avg')}`,
        ],
        datasets: [
          {
            data: [
              // roundBest ? roundBest.toFixed(1) : 0,
              roundBest ? unitConversion(this.unitType, roundBest, 'feet') : undefined,
              this.stats?.PuttingDistanceAverage === undefined
                ? 0
                : // : this.stats?.PuttingDistanceAverage?.toFixed(1),
                  unitConversion(
                    this.unitType,
                    this.stats?.PuttingDistanceAverage?.toFixed(1),
                    'feet'
                  ),
            ],
            backgroundColor: ['#231f20', this.doughnutBGColor],
          },
        ],
      };
    },

    puttingDistanceOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: `${this.$t('roundDetails.puttDist')} (${
              this.unitType === this.$t('roundDetails.imperial') ? 'ft' : 'm'
            })`,
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* Shot Line Chart Setup ==================
    shotDistance() {
      let userShots = this?.userShots ?? [];
      if (this.filteredClubType !== 'All') {
        userShots = this.userShots.filter((shot) => {
          return this.keyToFriendly(shot.clubTypeKey) === this.filteredClubType;
        });
      }

      //* Removing  && shot.score !== 0 as practice rounds are setting score to 0
      const filter = (shot) => {
        return shot.clubTypeKey !== 'Putter' && !shot.mulligan;
      };

      const distanceData = userShots.filter(filter, []).map((shot) => {
        return Math.round(shot.totalDistance / 3);
      }, []);

      const carryData = userShots.filter(filter, []).map((shot) => {
        return Math.round(shot.carryDistance / 3);
      }, []);

      const labels = userShots.filter(filter, []).map((shot) => {
        return shot.shotIndex + 1;
      }, []);

      return {
        labels,
        datasets: [
          {
            label: this.$t('roundDetails.distance'),
            borderColor: '#769CD8',
            backgroundColor: '#769CD8',
            pointRadius: 5,
            fill: false,
            deviationStrokeColor: '#000',
            data: distanceData,
            // steppedLine: 'before',
            lineTension: 0,
          },
          {
            label: this.$t('roundDetails.carry'),
            borderColor: '#7AD876',
            backgroundColor: '#7AD876',
            pointRadius: 5,
            fill: false,
            data: carryData,
            // steppedLine: 'before',
            lineTension: 0,
          },
        ],
      };
    },

    lineChartOptions() {
      let fontColor = '#424242';
      let delayed;
      if (this.$vuetify.theme.dark) {
        fontColor = '#ffffff';
      }
      const options = baseLineChartOptions(this.$vuetify.theme.dark);

      // const optionsYdsUnitType = this.unitType === 'Imperial' ? 'yds' : 'm';
      const optionsYdsUnitType = this.unitType === this.$t('roundDetails.imperial') ? 'yds' : 'm';
      const optionsYdsUnitConversion = (val, type) => {
        if (!val) return;
        switch (this.unitType) {
          case 'Imperial':
            if (type === 'yds') {
              return val?.toFixed(0);
            }
            return val;
          case 'Metric':
            if (type === 'yds') {
              //* yards to meters divide by 1.094
              return (val / 1.094).toFixed(0);
            }
            return val;
          default:
            return Math.abs(this.roundNum(val * 0.681818));
        }
      };

      //* Override animation delay time
      options.animation = {
        ...options.animation,
        delay: (context) => {
          let delay = 0;
          if (context.type === 'data' && context.mode === 'default' && !delayed) {
            delay = context.dataIndex * 50 + context.datasetIndex * 100;
          }
          return delay;
        },
      };

      //* Override Y scale tick option
      options.scales.y = {
        ticks: {
          color: fontColor,
        },
      };

      //* Override gap option to allow
      options.spanGaps = false;

      //* Override tooltip data
      options.plugins.tooltip.callbacks = {
        label(tooltipItem) {
          const { label = '' } = tooltipItem?.dataset;
          const { formattedValue } = tooltipItem;
          const yards = formattedValue * 1.0 || 0.0;
          // console.log('yards:', yards);
          // console.log(
          //   'label test:',
          //   `${label}: ${optionsYdsUnitConversion(
          //     Number(yards?.toFixed(0)),
          //     'yds'
          //   )}${optionsYdsUnitType}`
          // );
          return `${label}: ${optionsYdsUnitConversion(
            Number(yards?.toFixed(0)),
            'yds'
          )} ${optionsYdsUnitType}`;
          // return `${label}: ${yards.toFixed(0)}${optionsYdsUnitType}`;
        },
      };

      return options;
    },

    //* Par Area Chart Setup ==================
    roundParChartData() {
      let pars = [];

      if (this.scoreCardData?.holeRoundScores) {
        for (let i = 0; i < this.scoreCardData?.holeRoundScores.length; i++) {
          if (
            this.scoreCardData?.holeRoundScores[i] !== undefined &&
            this.scoreCardData?.holeRoundScores[i] !== null
          ) {
            pars = [...pars, this.scoreCardData?.holeRoundScores?.[i]];
            // pars.push(this.scoreCardData?.holeRoundScores[i]);
          }
        }
      }
      this.pars = pars;

      const labels = this.scoreCardData?.holeScores
        ?.map((shot, index) => {
          return shot !== null ? index + 1 : null;
        })
        .filter((data) => {
          return data !== null;
        });

      return {
        labels,
        datasets: [
          {
            label: 'Hole Par',
            borderColor: '#ba2127',
            backgroundColor: '#bcbec0',
            pointRadius: 5,
            fill: {
              target: 'origin',
              above: 'rgb(188,190,192)',
              below: 'rgb(120,176,88)',
            },
            deviationStrokeColor: '#000',
            data: this.pars,
            lineTension: 0,
          },
        ],
      };
    },

    areaParChartOptions() {
      const highestPar = Math.max.apply(null, this.pars);
      const lowestPar = Math.min.apply(null, this.pars);
      let maxPar = 4;
      let minPar = -3;
      const parAdjustment = 1;
      let fontColor = '#424242';
      if (this.$vuetify.theme.dark) {
        fontColor = '#ffffff';
      }

      maxPar = highestPar > 4 ? highestPar + parAdjustment : maxPar;
      minPar = lowestPar < -3 ? lowestPar - parAdjustment : minPar;

      const options = baseLineChartOptions(this.$vuetify.theme.dark, {
        isPar: true,
      });

      options.scales.y = {
        ticks: {
          color: fontColor,
          stepSize: 1,
          autoSkip: false,
        },
        min: minPar,
        max: maxPar,
      };
      return options;
    },

    // averageTotalDistance() {
    //   return this.averageData('totalDistance', '#769CD8', (val) => {
    //     return val / 3.0;
    //   });
    // },

    // averageCarryDistance() {
    //   return this.averageData('carryDistance', '#7AD876', (val) => {
    //     return val / 3.0;
    //   });
    // },

    // averageBackSpin() {
    //   return this.averageData('launchBackSpin', '#F49F57');
    // },

    // averageBallSpeed() {
    //   return this.averageData('launchBallSpeed', '#76D8D8', (val) => {
    //     return val * 0.681818;
    //   });
    // },

    isStrokePlay() {
      if (this.round) {
        return this.round?.gameMode === 'stroke_play';
      }
    },

    isScramble() {
      if (this.round) {
        return this.round?.gameMode?.toLowerCase() === 'scramble';
      }
    },

    isBestBall() {
      if (this.round) {
        return this.round?.gameMode === 'best_ball';
      }
    },

    isStableford() {
      if (this.round) {
        return this.round?.gameMode?.toLowerCase() === 'stableford';
      }
    },

    showScorecard() {
      if (this.round?.gameMode) {
        return this.isStrokePlay || this.isScramble || this.isBestBall || this.isStableford;
      }
    },

    roundPlayers() {
      if (this.round?.players) {
        return this.round.players;
      }
    },

    roundGuestPlayers() {
      if (this.round?.guests) {
        return this.round.guests;
      }
    },

    userShots() {
      let shotList = this?.shotData ?? [];
      let originalShotList = [];

      //* This will filter out action records of score
      //! Use this if merging in action records in store
      shotList = originalShotList = shotList.filter((shot) => {
        return shot.hasOwnProperty('shotIndex');
      });

      //* Filter by club.
      const clubTypes = new Set(['All']);
      if (this.filteredClubType !== 'All') {
        shotList = shotList.filter((shot) => {
          return this.keyToFriendly(shot.clubTypeKey) === this.filteredClubType;
        });
      }

      shotList.forEach((shot) => {
        return clubTypes.add(this.keyToFriendly(shot.clubTypeKey));
      });
      originalShotList.forEach((shot) => {
        return clubTypes.add(this.keyToFriendly(shot.clubTypeKey));
      });

      this.clubTypes = Array.from(clubTypes);

      // shotList = shotList.filter(shot => !shot.mulligan && shot.score !== 0)
      return shotList.filter((shot) => {
        return shot.userID === this.$route.params.userID;
      });
    },

    roundDetails() {
      // TODO pull in the tracking system with this or wait for it to be returned in round response
      // let shotList = [];
      // shotList = [...this.shotList];

      // const trackingSystemKey = shotList.reduce((acc, curVal, i, arr) => {
      //   acc = curVal.trackingSystemKey;
      //   return acc;
      // }, {});

      let player = {};

      if (this.round?.players !== undefined) {
        player = this.round?.players?.find((player) => {
          return player.id === this.$route.params.userID;
        });
      }

      return {
        title: `Round Details - ${this.round?.courseName}`,
        createdAt: this.round ? this.round?.createdAt : '',
        gameMode: this.utility_gameModeFormat(this.round?.gameMode),
        course: this.round?.course,
        mulliganSetting:
          this.round?.mulliganSetting < 0 ? 'Unlimited' : this.round?.mulliganSetting,
        teeLevel: player === undefined ? '' : player.teeLevel,
        fairwayHardness: this.round?.fairwayHardness,
        eventID: this.round?.eventID,
        elevation: `${this.round?.elevation} ft`,
        stimpRating: this.round?.stimpRating,
        greenHardness: this.round?.greenHardness,
        wind: this.round?.windSpeed,
        pinPosition: this.round?.pinSet ? this.round?.pinSet[0] + 1 : 0,
        puttingMode: this.computePuttingMode(this.round?.puttingMode),
        autoCaddie: this.round?.autoCaddy,
        scoringPenalty: this.round?.scoringPenalty,
        players: this.round?.players?.length,
        team: player?.team,
        eventEligible: player?.eligible ?? false,
        holeCountSelected: this.round?.holeSet?.length,
        selectedHoles: this.round?.holeSet,
        event: this.round?.event,
        roundHandicap: player?.handicap ?? null,
        qualifiedForHandicap: player?.qualifiedForHandicap,
        data: [
          [
            { header: 'Format', value: this.utility_gameModeFormat(this.round.gameMode) },
            { header: 'Tee Level', value: player === undefined ? '' : player.teeLevel },
            {
              header: 'Mulligan Setting',
              value: this.round?.mulliganSetting < 0 ? 'Unlimited' : this.round?.mulliganSetting,
            },
            { header: 'Fairway Hardness', value: this.round?.fairwayHardness },
            { header: 'Elevation', value: `${this.round?.elevation} ft` },
            { header: 'Stimp Rating', value: this.round?.stimpRating },
            { header: 'Green Hardness', value: this.round?.greenHardness },
            { header: 'Wind', value: this.round?.windSpeed },
            { header: 'Pin Position', value: this.round?.pinSet ? this.round?.pinSet[0] : 0 },
            { header: 'Putting Mode', value: this.computePuttingMode(this.round?.puttingMode) },
            // { header: 'Tracking System', value: trackingSystemKey },
            { header: 'AutoCaddie', value: 'N/A' },
            { header: 'Scoring Penalty', value: this.round?.scoringPenalty },
          ],
        ],
      };
    },

    // TODO potentially use this to show/hide tabs. Causing display issues currently
    filteredTabs() {
      let tabArr = [...this.tabItems];

      if (!this.isStrokePlay && !this.loading && !this.scoreCardLoading) {
        tabArr = this.tabItems.filter((tab) => {
          return tab.value !== 'scorecard';
        });
      }
      return tabArr;
    },

    hasShotGraphData() {
      return this.shotDistance?.datasets?.[0]?.data?.length > 0;
    },

    ftUnitType() {
      // @ unitType = Imperial return ft
      // @ unitType = Metric return m
      // @ default return ft
      return this.unitType === this.$t('roundDetails.imperial')
        ? 'ft'
        : this.unitType === this.$t('roundDetails.metric')
        ? 'm'
        : 'ft';
    },

    ydsUnitType() {
      // @ unitType = Imperial return yds
      // @ unitType = Metric return m
      // @ default return yds
      return this.unitType === this.$t('roundDetails.imperial')
        ? 'yds'
        : this.unitType === this.$t('roundDetails.metric')
        ? 'm'
        : 'yds';
    },
  },

  methods: {
    unitConversion,

    shotClick(shot) {
      // console.log('shot:', shot);
      this.shotIDProp = shot.id;
      this.dialog = true;
      this.isActive = shot.id;

      //* Route to user round
      // this.$router.push({
      //   name: 'shotview',
      //   props: true,
      //   params: {
      //     shotID: shot.id,
      //   },
      // });
    },

    closeDialog() {
      this.dialog = false;
      this.isActive = '';
    },

    getWidth() {
      //* Calculate a mid width for faux loading indicator for AA
      this.calcLeft = `${this.$refs.cardContainer.clientWidth / 2}px`;
    },

    next() {
      const active = parseInt(this.active);
      this.active = active < 2 ? active + 1 : 0;
    },

    async userScorecardData(player) {
      // console.log('player:', player);
      // console.log('User:', this.user);
      this.userShotsLoading = true;
      const { holes } = this.round.course || {};
      const { holeSet = [] } = this.round;
      const shotData = await scorecardDataConfig(player, holes, holeSet);

      if (shotData) {
        player.scoreCardData = this.scoreCardData = shotData;
        this.userShotsLoading = false;
        this.scoreCardLoading = false;
      }

      return shotData;
    },

    roundNum(num) {
      if (!num && num !== 0) return;
      return Number(num)?.toFixed(2);
    },

    keyToFriendly(key) {
      if (key === undefined) return key;
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => {
          return str.toUpperCase();
        })
        .trim();
    },

    computePuttingMode: (puttMode) => {
      switch (puttMode) {
        case 'playerPuttsOut':
          return 'Player Putts Out';
        case 'computerDecides':
          return 'Computer Decides';
        case 'autoScore':
          return 'Autoscore';
        case 'playerDecides':
          return 'Player Decides';
        case '2FootGimmie':
          return '2 Foot Gimme';
        case '5FootGimmie':
          return '5 Foot Gimme';
        case '10FootGimmie':
          return '10 Foot Gimme';
        case '15FootGimmie':
          return '15 Foot Gimme';
        case '20FootGimmie':
          return '20 Foot Gimme';
        default:
          return '-';
      }
    },

    togglePolling() {
      if (this.pollingHandler === null && this.polling) {
        this.pollingHandler = setInterval(() => {
          const { finalized } = this.round;
          if (finalized) {
            this.destroyPolling(this.pollingHandler);
            this.$root.vtoast.show({
              text: `Round finished. No more shots to record.`,
              color: 'info',
            });
          }

          let cacheBust;
          const browser = browserDetector();

          if (browser === 'IE 11') {
            cacheBust = `/?cb=${Date.now()}`;
          } else {
            cacheBust = '';
          }

          const payload = {
            roundID: `${this.$route.params.id}${cacheBust}`,
            userID: this.$route.params.userID,
          };

          // TODO If calling shots here, try not to in scorecard
          this.$store.dispatch('shots/all', payload).then((res) => {
            this.shotData = res;
          });

          const { players = [] } = this.round;
          const currentPlayer = players.find((player) => {
            return player.id === this.$route.params.userID;
          });
          const currentPlayerRoundHCP = currentPlayer?.handicap?.toFixed(1);
          this.userScorecardData({
            user_uuid: this.$route.params.userID,
            round_uuid: this.$route.params.id,
            handicap: currentPlayerRoundHCP || (this.user?.handicap ? this.user?.handicap : 0),
            gender: this.user?.gender,
            gameMode: this.roundGameMode,
            players: this.roundPlayers,
            guestPlayers: this.roundGuestPlayers,
          });
        }, 7000);
      } else {
        // this.pollingHandler = null;
        this.destroyPolling(this.pollingHandler);
      }
    },

    destroyPolling(handler) {
      clearInterval(handler);
      this.pollingHandler = false;
      this.pollingHandler = null;
      this.polling = false;
    },

    averageData(dataType, color, fnConvert) {
      let userShots = 0;
      let allShots = 0;
      let userTotal = 0;
      let allTotal = 0;
      let userAverage = 0;
      let allAverage = 0;
      const shotList = this?.shotData ?? [];

      if (shotList.length) {
        shotList.forEach((shot) => {
          const { clubTypeKey, mulligan, score, userID } = shot;
          //* Removing for testing -> score === 0 as practice rounds set score to 0
          // if (!shot[dataType] || clubTypeKey === 'Putter' || mulligan || score === 0) return;
          if (!shot[dataType] || clubTypeKey === 'Putter' || mulligan) return;
          if (this.filteredClubType !== 'All') {
            if (this.keyToFriendly(clubTypeKey) !== this.filteredClubType) return;
          }
          if (userID === this.$route.params.userID) {
            userShots++;
            userTotal += shot[dataType];
          }
          allShots++;
          allTotal += shot[dataType];
        });
      }

      userAverage = userTotal / userShots;
      allAverage = allTotal / allShots;
      if (typeof fnConvert === 'function') {
        userAverage = fnConvert(userAverage);
        allAverage = fnConvert(allAverage);
      }
      if (this.userShots.length === 0) {
        userAverage = 0;
        allAverage = 0;
      }

      return {
        labels: [`User Average`, `Round Average`],
        datasets: [
          {
            data: [Math.round(userAverage), Math.round(allAverage)],
            backgroundColor: [color, 'rgba(0, 0, 0, 0.2)'],
          },
        ],
      };
    },

    back() {
      this.$router.back();
    },

    checkTerrain(check, terrain) {
      return check && check.terrainType === terrain;
    },

    subscribeAA() {
      console.log('subscribe to AA');
    },

    onResize() {
      if (window.innerWidth < 601) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    },

    mulliganClass(item) {
      return item.mulligan ? 'mulligan pointer' : 'pointer';
    },

    clickedGolferRow(item) {
      this.$router.push({
        name: 'userProfile',
        path: `/users/${item.id}`,
        props: true,
        params: {
          id: `${item.id}`,
        },
      });
    },
  },

  filters: {
    nan(val) {
      if (isNaN(val)) return '-';
      return val;
    },
  },

  watch: {
    polling(newVal, oldVal) {
      if (!newVal) {
        clearInterval(this.pollingHandler);
      }
    },

    //* Fake a loading indicator for AA as it's out of our state management
    active(newVal, oldVal) {
      if (newVal === 2) {
        this.getWidth();
        this.loadingAA = true;
        setTimeout(() => {
          this.loadingAA = false;
        }, 2500);
      }
    },

    unitType(newVal) {
      //* set local storage
      localStorage.setItem('e6UserMeasurement', newVal);
    },
  },
};
</script>

<style scoped>
/* helps with overlapping wording on table */
td,
tr {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.round-details {
  background: transparent !important;
  background-color: transparent !important;
}

.round-details-tabs .poll {
  white-space: normal;
  letter-spacing: normal;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1.5rem;
  align-items: center;
  text-align: center;
  height: 48px !important;
}

.progress {
  display: flex;
  align-items: center;
  justify-content: center;
}

.progressAA {
  position: absolute;
  z-index: 1;
  top: 25%;
  /* left: 40vw; */
  width: 100%;
}

.progress-main {
  margin-top: 100px;
}

.progress-scorecard {
  padding-top: 50px;
  margin-bottom: 50px;
}

.golfer,
tr.golfer > td {
  cursor: pointer;
}

.fillTD {
  display: block;
  padding: 10px 0;
}

.flex-between {
  display: flex;
  justify-content: space-between;
}

.custom-flex-between {
  display: flex;
  justify-content: space-between;
}

.donuts {
  margin-top: 48px;
}

.d-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, 60%);
  font-size: 20px;
}
#doughnut-chart,
.dg-chart {
  position: relative;
  z-index: 100;
}

.rel {
  position: relative;
}

/* .data-table {
  margin-top: 48px;
} */

/* moved to mainStyles */
/* .mulligan {
  opacity: 0.4;
} */

.auto-update {
  margin-right: 24px;
  margin-top: -55px;
  float: right;
}

td span {
  transition: all 150ms ease-in;
}

td span.active {
  font-weight: bold;
  font-size: 0.9rem;
  color: var(--primary, #ba2127);
}

.poll {
  margin-right: 12px;
  margin-top: 4px;
}

.theme--dark .poll {
  color: var(--white);
}

/* .line-chart {
  margin-top: 24px;
} */

.filters {
  margin-left: 24px;
  width: 250px;
}

.filter-by {
  font-size: 16px;
  margin-top: 22px;
  /* margin-right: 12px; */
  /* width: 75px; */
}

.wrap {
  flex-wrap: wrap;
}

.v-input--selection-controls {
  margin-top: 0;
}

.shot-info-dialog {
  z-index: 99999 !important;
}

.analytics-card {
  border-top-right-radius: 0;
  border-top-left-radius: 0;
}

* >>> .v-tab:not(.v-tab--active) {
  color: rgba(255, 255, 255, 0.8) !important;
}

* >>> .v-card:not(.shot-card) {
  border: none;
}

* >>> .v-tabs__wrapper {
  padding-left: 20px;
}

* >>> .v-tabs-slider {
  height: 3px;
}

* >>> .v-card > :first-child:not(.v-btn):not(.v-chip) {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

* >>> .theme--light.v-data-table.shot-data-table tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.03);
}

* >>> .theme--dark.v-data-table.shot-data-table tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.5);
}

@media screen and (max-width: 1280px) {
  .d-title {
    top: 57%;
    font-size: 16px;
  }
}

@media screen and (max-width: 1024px) {
  .scorecard-container {
    display: flex;
    overflow: auto;
  }

  .d-title {
    top: 62%;
    font-size: 0.875rem;
  }
}

@media screen and (max-width: 960px) {
  .d-title {
    top: 50%;
    font-size: 1.375rem;
  }
}

@media screen and (min-width: 600px) {
  .scorecard-rotate {
    display: none;
  }
}

@media screen and (max-width: 600px) {
  * >>> .v-slide-group__prev {
    display: none !important;
  }

  * >>> .v-tabs__wrapper {
    padding-left: 0;
    overflow: auto;
  }
}

@media (max-width: 415px) {
  .auto-update {
    margin-top: 0px;
    float: left;
    margin-left: 24px;
  }
}
</style>
